import { useCallback, useEffect, useState } from 'react';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { Report, ReportId, ReportStatus } from '@hcs/types';

import { ReportApi } from '../api';

import { useSubscribeToReportEvents } from './useSubscribeToReportEvents';

export const QUERY_KEY_REPORT = 'report-api-report';
export const generateQueryKeyReport = (reportId: ReportId) => {
  return [QUERY_KEY_REPORT, reportId];
};
export const useReport = (
  reportId: ReportId,
  options?: UseQueryOptions<
    Report,
    AxiosError<{
      status: string;
      message?: string;
      expired?: string;
    }>,
    Report,
    ReportId[]
  >,
) => {
  const [reportCreationComplete, setReportCreationComplete] = useState<
    null | boolean
  >(null);
  const queryClient = useQueryClient();
  useSubscribeToReportEvents({
    callbackId: QUERY_KEY_REPORT,
    onMessage: useCallback(
      // Handler to update the query cache for this hook when we receive relevant SSEs
      (reportEvent) => {
        const queryKey = generateQueryKeyReport(reportId);
        const reportStatus = reportEvent.report.status;
        // Update report metadata when receiving SSEs about the report
        if (reportStatus === ReportStatus.Error) {
          queryClient.refetchQueries({ queryKey: queryKey });
        } else if (
          reportEvent.type === 'DocumentsRefreshed' ||
          reportEvent.type === 'DocumentsUpdated' ||
          reportEvent.type === 'DocumentsCreated' ||
          reportEvent.type === 'DocumentsDeleted'
        ) {
          // Only refresh document data after the refresh action is done
          if (reportEvent.patchStatus === 'DONE') {
            queryClient.refetchQueries({ queryKey: queryKey });
          }
        }
      },
      [reportId, queryClient],
    ),
  });
  const reportQuery = useQuery({
    queryKey: generateQueryKeyReport(reportId),

    queryFn: async () => {
      const report = await ReportApi.fetchReport(reportId);
      return {
        ...report,
      };
    },
    ...options,
    refetchInterval: reportCreationComplete === false ? 5000 : false,
  });

  useEffect(() => {
    if (reportQuery.data?.status === ReportStatus.Completed) {
      setReportCreationComplete(true);
    } else if (reportQuery.data?.status === ReportStatus.InProgress) {
      setReportCreationComplete(false);
    }
  }, [reportQuery.data?.status]);

  return reportQuery;
};
